/**
 * Where the columns go: a frame measured in real pixels (see useMeasuredWidth), round ticks from
 * d3, and the few positions every part of the chart shares. Pure, so it is tested without a DOM.
 */
import { niceMax, ticks } from './scale';

export const PAD = { top: 26, right: 12, bottom: 24, left: 44 };
export const PAD_NARROW = { top: 26, right: 8, bottom: 22, left: 8 };

/** The widest a column grows, so a ramp of two months is two columns, not two slabs. */
export const MAX_BAR = 48;

export interface Frame {
  width: number;
  height: number;
  top: number;
  step: number;
  barW: number;
  innerH: number;
  left: number;
  narrow: boolean;
  /** Whole things on the axis (companies): whole ticks only. */
  counts: boolean;
  /** Label every this many columns, when the chart says so (a histogram labels every bin). */
  axisEvery: number | undefined;
}

/** How a chart asks for its axis: counts, and a fixed label spacing. */
export interface FrameOptions {
  counts?: boolean | undefined;
  axisEvery?: number | undefined;
}

/** The frame for `count` columns over these values (the columns, the line, the ghost and the reference). */
export function frameOf(
  width: number,
  height: number,
  count: number,
  values: readonly number[],
  options: FrameOptions = {},
): Frame {
  const narrow = width < 480;
  const pad = narrow ? PAD_NARROW : PAD;
  const step = (width - pad.left - pad.right) / Math.max(1, count);
  const gap = step > 8 ? Math.max(2, step * 0.22) : 1;
  return {
    width,
    height,
    top: niceMax(values),
    step,
    barW: Math.min(MAX_BAR, Math.max(1, step - gap)),
    innerH: height - pad.top - pad.bottom,
    left: pad.left,
    narrow,
    counts: options.counts ?? false,
    axisEvery: options.axisEvery,
  };
}

export const yOf = (f: Frame, v: number) =>
  f.height - (f.narrow ? PAD_NARROW : PAD).bottom - f.innerH * Math.min(1, v / f.top);
export const xOf = (f: Frame, i: number) => f.left + i * f.step + (f.step - f.barW) / 2;
export const midOf = (f: Frame, i: number) => xOf(f, i) + f.barW / 2;
export const baseOf = (f: Frame) => f.height - (f.narrow ? PAD_NARROW : PAD).bottom;

/** The column under an x in the frame's own pixels, clamped to the ends. */
export function indexAt(f: Frame, x: number, count: number): number {
  return Math.min(count - 1, Math.max(0, Math.floor((x - f.left) / f.step)));
}

/** A column with 4px-rounded top corners standing on the baseline; square when it is too thin to round. */
export function columnPath(x: number, y: number, w: number, h: number): string {
  const r = Math.min(4, w / 2, h);
  return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
}

/**
 * Axis labels that fit: about one per 72px, counted back from the last so the latest is always
 * labelled; or every `axisEvery` columns when the chart asks, so a histogram labels each bin at its
 * centre instead of leaving a bar between two labels.
 */
export function labelEvery(f: Frame, count: number): number {
  if (f.axisEvery !== undefined) return Math.max(1, f.axisEvery);
  return Math.max(1, Math.ceil(72 / Math.max(1, f.step)), Math.ceil(count / 14));
}

/** The grid's round values: three on a phone, four on a desk. */
export const gridTicks = (f: Frame) => ticks(f.top, f.narrow ? 3 : 4, { counts: f.counts });

/** The left and right edges of a run of columns, in the frame's own pixels. */
export const spanEdges = (f: Frame, from: number, to: number): [number, number] => [
  f.left + from * f.step,
  f.left + (to + 1) * f.step,
];
